import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ACTIVITY_TITLE,
  formatDayMonthShort,
  formatNumber,
  localNow,
  minutesBetween,
  parseNumericInput,
  type ActivityKind,
  type Exercise,
  type Workout,
  type WorkoutExercise,
} from '@jungles/shared';
import { api } from '../lib/api';
import { useApi, useAutosave } from '../lib/hooks';
import { useUser } from '../app/session';
import { useToast } from '../app/toast';
import { hapticSuccess } from '../lib/telegram';
import { BackLink, ErrorState, Header, Loader, Screen, TabBar } from '../components/Chrome';
import { Dialog } from '../components/Sheet';
import { ChevronIcon, GymIcon } from '../components/icons';
import { ExercisePicker } from '../components/ExercisePicker';
import { GymPicker } from '../components/GymPicker';

/**
 * T1–T5 · Запись тренировки: своя, готовая и другая активность.
 * Один экран на три режима — отличается только состав полей (SPEC §5).
 */

const ACTIVITY_KINDS: ActivityKind[] = ['run', 'walk', 'bike', 'swim', 'other'];

type DraftExercise = Omit<WorkoutExercise, 'sets'> & {
  sets: (Omit<WorkoutExercise['sets'][number], 'weightKg' | 'reps'> & {
    weightKg: string;
    reps: string;
  })[];
};

function optionalNumber(text: string, integer = false): number | null {
  return text.trim() === '' ? null : parseNumericInput(text, integer);
}

function validNumber(text: string, max: number, integer = false): boolean {
  const value = optionalNumber(text, integer);
  return value === null || (Number.isFinite(value) && value >= 0 && value <= max);
}

function validDraft(draft: Draft): boolean {
  return validNumber(draft.durationMin, 1440, true) && validNumber(draft.distanceKm, 1000) &&
    draft.exercises.every(exercise => exercise.sets.every(set =>
      validNumber(set.weightKg, 1000) && validNumber(set.reps, 1000, true)));
}

function timeOf(iso: string | null, timezone: string): string {
  if (!iso) return '';
  return localNow(timezone, new Date(iso)).time;
}

interface Draft {
  date: string;
  startTime: string;
  endTime: string;
  durationMin: string;
  gymId: string;
  /** Название держим в черновике: справочник с сервера, по id его не найти. */
  gymName: string;
  activityKind: ActivityKind;
  distanceKm: string;
  exercises: DraftExercise[];
}

function toDraft(workout: Workout, timezone: string): Draft {
  return {
    date: workout.date,
    startTime: timeOf(workout.startedAt, timezone),
    endTime: timeOf(workout.endedAt, timezone),
    durationMin: workout.durationMin === null ? '' : String(workout.durationMin),
    gymId: workout.gymId ?? '',
    gymName: workout.gymName ?? '',
    activityKind: workout.activityKind ?? 'run',
    distanceKm: workout.distanceKm === null ? '' : String(workout.distanceKm),
    exercises: workout.exercises.map(exercise => ({ ...exercise, sets: exercise.sets.map(set => ({
      ...set, weightKg: set.weightKg === null ? '' : String(set.weightKg),
      reps: set.reps === null ? '' : String(set.reps),
    })) })),
  };
}

function volumeOf(exercises: DraftExercise[]): number {
  return exercises.reduce(
    (total, exercise) =>
      total +
      exercise.sets.reduce(
        (sum, set) =>
          validNumber(set.weightKg, 1000) && validNumber(set.reps, 1000, true)
            ? sum + (optionalNumber(set.weightKg) ?? 0) * (optionalNumber(set.reps, true) ?? 0)
            : sum,
        0,
      ),
    0,
  );
}

export function WorkoutScreen() {
  const { id = '' } = useParams();
  const user = useUser();
  const navigate = useNavigate();
  const toast = useToast();

  const { data: workout, error, loading, setData } = useApi<Workout>(`/api/workouts/${id}`);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [picker, setPicker] = useState(false);
  const [gymPicker, setGymPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (workout && !draft) setDraft(toDraft(workout, user.timezone));
  }, [workout, draft, user.timezone]);

  const persist = useCallback(
    async (value: Draft) => {
      // Invalid raw text stays editable, but must never become JSON null or a stale save.
      if (!validDraft(value)) return;
      const payload = {
        date: value.date,
        startTime: value.startTime || null,
        endTime: value.endTime || null,
        durationMin: optionalNumber(value.durationMin, true),
        gymId: value.gymId || null,
        activityKind: workout?.type === 'activity' ? value.activityKind : undefined,
        distanceKm: optionalNumber(value.distanceKm),
        exercises: value.exercises.map((exercise, index) => ({
          exerciseId: exercise.exerciseId,
          order: index + 1,
          restSec: exercise.restSec,
          targetSets: exercise.targetSets,
          targetReps: exercise.targetReps,
          sets: exercise.sets.map((set, setIndex) => ({
            order: setIndex + 1,
            weightKg: optionalNumber(set.weightKg),
            reps: optionalNumber(set.reps, true),
            done: set.done,
          })),
        })),
      };
      const updated = await api.patch<Workout>(`/api/workouts/${id}`, payload);
      setData(updated);
    },
    [id, setData, workout?.type],
  );

  const { schedule, flush, saving } = useAutosave(persist);

  const update = useCallback(
    (patch: Partial<Draft>) => {
      setDraft((current) => {
        if (!current) return current;
        const next = { ...current, ...patch };
        schedule(next);
        return next;
      });
    },
    [schedule],
  );

  const totalVolume = useMemo(() => (draft ? volumeOf(draft.exercises) : 0), [draft]);

  if (loading || !draft || !workout) {
    return (
      <Screen tabs>
        <Header />
        {error ? <ErrorState message={error} /> : <Loader />}
        <TabBar />
      </Screen>
    );
  }

  const isActivity = workout.type === 'activity';
  const isReady = workout.type === 'ready';
  const isDone = workout.status === 'done';
  const numericValid = validDraft(draft);

  const addExercise = (exercise: Exercise) => {
    setPicker(false);
    update({
      exercises: [
        ...draft.exercises,
        {
          id: `new-${exercise.id}-${draft.exercises.length}`,
          exerciseId: exercise.id,
          name: exercise.name,
          icon: exercise.icon,
          order: draft.exercises.length + 1,
          restSec: null,
          targetSets: null,
          targetReps: null,
          previous: null,
          sets: [{ id: 'new-1', order: 1, weightKg: '', reps: '', done: false }],
        },
      ],
    });
  };

  const removeExercise = (index: number) => {
    update({ exercises: draft.exercises.filter((_, i) => i !== index) });
  };

  const addSet = (index: number) => {
    const exercises = draft.exercises.map((exercise, i) => {
      if (i !== index) return exercise;
      const last = exercise.sets[exercise.sets.length - 1];
      return {
        ...exercise,
        sets: [
          ...exercise.sets,
          {
            id: `new-${exercise.sets.length + 1}-${Date.now()}`,
            order: exercise.sets.length + 1,
            weightKg: last?.weightKg ?? '',
            reps: last?.reps || (exercise.targetReps === null ? '' : String(exercise.targetReps)),
            done: false,
          },
        ],
      };
    });
    update({ exercises });
  };

  const patchSet = (
    exerciseIndex: number,
    setIndex: number,
    patch: Partial<{ weightKg: string; reps: string; done: boolean }>,
  ) => {
    const exercises = draft.exercises.map((exercise, i) =>
      i !== exerciseIndex
        ? exercise
        : {
            ...exercise,
            sets: exercise.sets.map((set, j) => (j !== setIndex ? set : { ...set, ...patch })),
          },
    );
    update({ exercises });
  };

  const removeSet = (exerciseIndex: number, setIndex: number) => {
    const exercises = draft.exercises.map((exercise, i) =>
      i !== exerciseIndex
        ? exercise
        : { ...exercise, sets: exercise.sets.filter((_, j) => j !== setIndex) },
    );
    update({ exercises });
  };

  const finish = async () => {
    if (!numericValid) return;
    setBusy(true);
    try {
      await flush();
      await api.post(`/api/workouts/${id}/finish`);
      hapticSuccess();
      toast('Тренировка записана');
      navigate('/workouts/history', { replace: true });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось завершить');
      setBusy(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!numericValid) return;
    try {
      await flush();
      await api.post('/api/templates', { workoutId: id });
      toast('Сохранили как шаблон');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось сохранить шаблон');
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/workouts/${id}`);
      toast(isDone ? 'Тренировка удалена' : 'Черновик удалён');
      navigate('/workouts', { replace: true });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось удалить');
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const duration =
    draft.startTime && draft.endTime ? minutesBetween(draft.startTime, draft.endTime) : null;

  return (
    <Screen tabs>
      <Header />
      <BackLink to="/workouts" label="Тренировки" />

      {isReady ? <div className="eb">Готовая тренировка</div> : null}
      <h1>{workout.title}</h1>
      <p className="nt" style={{ margin: '0 0 12px' }}>
        {isDone
          ? `Запись сохранена${saving ? ' · сохраняем правки…' : ''}`
          : saving
            ? 'Сохраняем черновик…'
            : 'Черновик сохраняется автоматически'}
      </p>

      <section className={isActivity ? 'card' : 'card workout-details'}>
        {isActivity ? (
          <div className="fields">
            <label className="fl">
              Дата
              <input
                className="f"
                type="date"
                value={draft.date}
                onChange={(event) => update({ date: event.target.value })}
              />
            </label>
            <label className="fl">
              Начало
              <input
                className="f"
                type="time"
                value={draft.startTime}
                onChange={(event) => update({ startTime: event.target.value })}
              />
            </label>
            <label className="fl">
              Длительность, мин
              <input
                className="f"
                type="text"
                inputMode="numeric"
                aria-invalid={!validNumber(draft.durationMin, 1440, true)}
                value={draft.durationMin}
                onChange={(event) => update({ durationMin: event.target.value })}
              />
            </label>
            <label className="fl">
              Вид активности
              <select
                className="f"
                value={draft.activityKind}
                onChange={(event) =>
                  update({ activityKind: event.target.value as ActivityKind })
                }
              >
                {ACTIVITY_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {ACTIVITY_TITLE[kind]}
                  </option>
                ))}
              </select>
            </label>
            <label className="fl" style={{ gridColumn: '1 / -1' }}>
              Расстояние, км <span className="mu">· необязательно</span>
              <input
                className="f"
                type="text"
                inputMode="decimal"
                aria-invalid={!validNumber(draft.distanceKm, 1000)}
                value={draft.distanceKm}
                onChange={(event) => update({ distanceKm: event.target.value })}
              />
            </label>
          </div>
        ) : (
          <div className="workout-times">
            <label className="fl sm">
              Дата
              <input
                className="f sm"
                type="date"
                value={draft.date}
                onChange={(event) => update({ date: event.target.value })}
              />
            </label>
            <label className="fl sm">
              Начало
              <input
                className="f sm"
                type="time"
                value={draft.startTime}
                onChange={(event) => update({ startTime: event.target.value })}
              />
            </label>
            <label className="fl sm">
              Окончание
              <input
                className="f sm"
                type="time"
                value={draft.endTime}
                onChange={(event) => update({ endTime: event.target.value })}
              />
            </label>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <button className="gymf gbtn" type="button" onClick={() => setGymPicker(true)}>
            <GymIcon />
            <span className="gl">Зал</span>
            <span className="gval">
              {draft.gymName || 'Без зала · дома или на улице'}
              {draft.gymId && draft.gymId === user.homeGymId ? ' · мой зал' : ''}
            </span>
            <ChevronIcon />
          </button>
        </div>

        {duration !== null ? (
          <p className="nt" style={{ margin: '10px 2px 0' }}>
            Длительность: {duration} мин
            {totalVolume > 0 ? ` · объём ${formatNumber(Math.round(totalVolume))} кг` : ''}
          </p>
        ) : null}
      </section>

      {!isActivity ? (
        <>
          {isReady ? (
            <p className="nt" style={{ margin: '0 0 14px' }}>
              Упражнения, подходы и повторы заданы программой. Впишите рабочий вес — повторы
              можно поправить.
            </p>
          ) : null}

          {draft.exercises.map((exercise, exerciseIndex) => (
            <section className="card" key={exercise.id}>
              <div className="row">
                <div className="exh">
                  <span className="exi" aria-hidden="true">
                    <img src={exercise.icon} alt="" />
                  </span>
                  <h3>
                    {exerciseIndex + 1}. {exercise.name}
                  </h3>
                </div>
                {isReady && exercise.targetSets ? (
                  <span className="pill bl">
                    {exercise.targetSets} × {exercise.targetReps ?? 'макс'}
                  </span>
                ) : (
                  <button
                    className="b ic"
                    type="button"
                    onClick={() => removeExercise(exerciseIndex)}
                    aria-label="Удалить упражнение"
                  >
                    ×
                  </button>
                )}
              </div>

              <span className="prev">
                {exercise.previous
                  ? `Прошлый раз · ${formatDayMonthShort(exercise.previous.date)}: ${
                      exercise.previous.weightKg ?? 0
                    } кг × ${exercise.previous.reps ?? 0}`
                  : 'Первый раз — прошлого результата нет'}
              </span>

              <div className={isReady ? 'sh ro' : 'sh'} aria-hidden="true">
                <span>#</span>
                <span style={{ textAlign: 'center' }}>Вес, кг</span>
                <span style={{ textAlign: 'center' }}>Повторы</span>
                {isReady ? null : <span />}
              </div>

              {exercise.sets.map((set, setIndex) => (
                <div
                  className={`${isReady ? 'set ro' : 'set'}${set.done ? ' done' : ''}`}
                  key={set.id}
                >
                  <span className="n">{setIndex + 1}</span>
                  <input
                    className="f mid"
                    inputMode="decimal"
                    aria-label={`Подход ${setIndex + 1}, вес`}
                    value={set.weightKg}
                    aria-invalid={!validNumber(set.weightKg, 1000)}
                    onChange={(event) =>
                      patchSet(exerciseIndex, setIndex, {
                        weightKg: event.target.value,
                      })
                    }
                  />
                  <input
                    className={isReady ? 'f mid fixed' : 'f mid'}
                    inputMode="numeric"
                    aria-label={`Подход ${setIndex + 1}, повторы`}
                    value={set.reps}
                    aria-invalid={!validNumber(set.reps, 1000, true)}
                    onChange={(event) =>
                      patchSet(exerciseIndex, setIndex, {
                        reps: event.target.value,
                      })
                    }
                  />
                  {isReady ? null : (
                    <button
                      className="tick"
                      type="button"
                      aria-label={set.done ? 'Отменить выполнение' : 'Отметить выполненным'}
                      aria-pressed={set.done}
                      onClick={() => patchSet(exerciseIndex, setIndex, { done: !set.done })}
                      onDoubleClick={() => removeSet(exerciseIndex, setIndex)}
                    >
                      ✓
                    </button>
                  )}
                </div>
              ))}

              <button
                className="b"
                type="button"
                style={{ width: '100%', marginTop: 6 }}
                onClick={() => addSet(exerciseIndex)}
              >
                + Подход
              </button>

              {exercise.restSec ? (
                <p className="nt" style={{ margin: '8px 0 0' }}>
                  Отдых {exercise.restSec} с
                </p>
              ) : null}
            </section>
          ))}

          <button className="b dash" type="button" onClick={() => setPicker(true)}>
            + Добавить упражнение
          </button>
        </>
      ) : null}

      {!numericValid ? <p className="err" role="alert">Проверьте числа: вес и расстояние — от 0 до 1000; повторы — целое от 0 до 1000; минуты — целое от 0 до 1440. Правки пока не сохранены.</p> : null}
      <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
        {isDone ? (
          <button
            className="b p big"
            type="button"
            onClick={() => navigate('/workouts/history')}
            disabled={busy || !numericValid}
          >
            Готово
          </button>
        ) : (
          <button className="b p big" type="button" onClick={() => void finish()} disabled={busy || !numericValid}>
            Завершить тренировку
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {!isActivity ? (
            <button
              className="b"
              type="button"
              style={{ flexGrow: 1 }}
              onClick={() => void saveAsTemplate()}
              disabled={busy || !numericValid}
            >
              Сохранить как шаблон
            </button>
          ) : null}
          <button className="b d" type="button" onClick={() => setConfirmDelete(true)}>
            Удалить
          </button>
        </div>
      </div>

      <TabBar />

      {picker ? (
        <ExercisePicker onPick={addExercise} onClose={() => setPicker(false)} />
      ) : null}

      {gymPicker && draft ? (
        <GymPicker
          currentId={draft.gymId || null}
          onPick={(gym) => {
            setGymPicker(false);
            update({ gymId: gym?.id ?? '', gymName: gym?.name ?? '' });
          }}
          onClose={() => setGymPicker(false)}
        />
      ) : null}

      {confirmDelete ? (
        <Dialog
          title={isDone ? 'Удалить тренировку?' : 'Удалить черновик?'}
          confirmLabel="Удалить"
          danger
          busy={busy}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => void remove()}
        >
          <p>
            {isDone
              ? 'Обновятся минуты, объём и игровые очки. Остальные записи останутся.'
              : 'Черновик не попал в историю — удаление ни на что не повлияет.'}
          </p>
        </Dialog>
      ) : null}
    </Screen>
  );
}
